import { safeLocalStorage, safeSessionStorage, storageDegraded } from '../lib/safeStorage';
import { clearWorkspace, bindWorkspaceOwner, rememberReturnPath } from '../lib/workspaceStorage';
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { hasPermission } from '../lib/permissions';
import { PROFILE_GATE_EVENT, isProfileGateError, gateFromError } from '../lib/profileGate';

const AuthContext = createContext(null);

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ---- Token storage helpers ----
const ACCESS_KEY = 'mm_access_token';
const REFRESH_KEY = 'mm_refresh_token';

export const getAccessToken = () => safeLocalStorage.getItem(ACCESS_KEY);
export const getRefreshToken = () => safeLocalStorage.getItem(REFRESH_KEY);
let authEpoch = 0;
// A11: aynı oturumda profil kapısı her güncellendiğinde artar; geç gelen /auth/me yanıtı daha yeni kapıyı ezmez.
let gateEpoch = 0;
const invalidateAuthWork = () => { authEpoch += 1; };
const setTokens = (access, refresh) => {
  if (access) safeLocalStorage.setItem(ACCESS_KEY, access);
  if (refresh) safeLocalStorage.setItem(REFRESH_KEY, refresh);
};
const clearTokens = () => {
  invalidateAuthWork();
  safeLocalStorage.removeItem(ACCESS_KEY);
  safeLocalStorage.removeItem(REFRESH_KEY);
};

// ---- Axios setup ----
// Module-level singleton: shared refresh promise so concurrent 401s
// trigger only one /auth/refresh call.
let refreshPromise = null;

const isAuthEndpoint = (url = '') =>
  url.includes('/api/auth/login') ||
  url.includes('/api/auth/register') ||
  url.includes('/api/auth/refresh') ||
  url.includes('/api/auth/forgot-password') ||
  url.includes('/api/auth/reset-password') ||
  url.includes('/api/auth/staff-invite/') ||
  url.includes('/api/claim-invites/check') ||
  url.includes('/api/claim-invites/redeem-register') ||
  url.includes('/api/auth/google/');

const onAuthPage = () => {
  const p = window.location.pathname;
  return ['/login', '/register', '/', '/forgot-password', '/reset-password', '/auth/google', '/personel-davet', '/kart-davet', '/mekan-davet'].includes(p);
};

// Scope credentials and refresh handling to our API origins and path boundary.
// Axios joins baseURL and relative URLs (even a leading /), unlike new URL alone.
const isBackendApiRequest = (config = {}) => {
  try {
    const url = config.url || '';
    const absolute = /^([a-z][a-z\d+.-]*:)?\/\//i.test(url);
    const target = new URL(!absolute && config.baseURL
      ? `${config.baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`
      : url, window.location.origin);
    const backend = new URL(BACKEND || window.location.origin, window.location.origin);
    return ['http:', 'https:'].includes(target.protocol) &&
      [backend.origin, window.location.origin].includes(target.origin) &&
      (target.pathname === '/api' || target.pathname.startsWith('/api/'));
  } catch (_) { return false; }
};

// Request interceptor: attach Authorization header from localStorage
axios.interceptors.request.use((config) => {
  if (isBackendApiRequest(config)) {
    config._authEpoch ??= authEpoch;
    config._authRefresh ??= getRefreshToken();
    const token = getAccessToken();
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Response interceptor: on 401 try refresh, then retry once
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || '';

    // A5b: eski sekmede kalan kullanıcı korumalı bir işlem denerse sunucu 409 döner; kapı durumu güncellenir
    // ve ProtectedRoute kullanıcıyı profil tamamlama ekranına götürür. Hata çağırana yine iletilir.
    if (isProfileGateError(error) && isBackendApiRequest(original)) {
      try { window.dispatchEvent(new CustomEvent(PROFILE_GATE_EVENT, { detail: gateFromError(error) })); } catch (_) { /* ignore */ }
    }
    if (status !== 401 || original._skipAuthRefresh || original._retry || !isBackendApiRequest(original) || isAuthEndpoint(url)) {
      return Promise.reject(error);
    }

    const epoch = authEpoch;
    const refreshToken = getRefreshToken();
    if ((original._authEpoch != null && original._authEpoch !== epoch) ||
        (Object.prototype.hasOwnProperty.call(original, '_authRefresh') && original._authRefresh !== refreshToken)) return Promise.reject(error);
    original._retry = true;
    const stillCurrent = () => authEpoch === epoch && getRefreshToken() === refreshToken;

    if (!refreshToken) {
      // No refresh token — session truly gone
      clearTokens();
      if (!onAuthPage()) {
        toast.dismiss('session-expired');
        toast.error('Oturum süreniz doldu. Lütfen tekrar giriş yapın.', { id: 'session-expired' });
        rememberReturnPath();
        const redirectEpoch = authEpoch;
        setTimeout(() => { if (authEpoch === redirectEpoch && !getAccessToken()) window.location.replace('/login'); }, 1500);
      }
      return Promise.reject(error);
    }

    try {
      if (!refreshPromise || refreshPromise.token !== refreshToken || refreshPromise.epoch !== epoch) {
        const job = { token: refreshToken, epoch };
        job.promise = axios.post(`${BACKEND}/api/auth/refresh`, { refresh_token: refreshToken }, { timeout: 15000 })
          .then(res => {
            if (!stillCurrent()) throw new Error('Oturum değişti; eski istek iptal edildi.');
            if (!res.data?.access_token) throw new Error('Oturum yenilenemedi.');
            setTokens(res.data.access_token, null);
            return res;
          }).finally(() => { if (refreshPromise === job) refreshPromise = null; });
        refreshPromise = job;
      }
      await refreshPromise.promise;
      if (!stillCurrent()) return Promise.reject(error);
      // Update Authorization header on the original request
      const newAccess = getAccessToken();
      if (newAccess) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
      }
      return axios(original);
    } catch (refreshErr) {
      if (!stillCurrent()) return Promise.reject(error);
      if (![400, 401, 403].includes(refreshErr.response?.status)) {
        return Promise.reject(refreshErr);
      }
      clearTokens();
      const SESSION_MSG = 'Oturum süreniz doldu. Lütfen tekrar giriş yapın.';
      try {
        error.response = error.response || {};
        error.response.data = { ...(error.response.data || {}), detail: SESSION_MSG };
      } catch (_) { /* ignore */ }

      if (!onAuthPage()) {
        toast.dismiss('session-expired');
        toast.error(SESSION_MSG, { id: 'session-expired' });
        rememberReturnPath();
        const redirectEpoch = authEpoch;
        setTimeout(() => { if (authEpoch === redirectEpoch && !getAccessToken()) window.location.replace('/login'); }, 1500);
      }
      return Promise.reject(error);
    }
  }
);

// The server derives role/staff permissions from the database on every request;
// these fields only drive menus. Older responses without them mean "no staff access".
export const withAccess = (user) => user ? {
  ...user,
  staff_role: user.staff_role ?? null,
  permissions: Array.isArray(user.permissions) ? user.permissions : [],
  is_main_admin: user.role === 'admin',
} : user;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Bir hata oluştu. Lütfen tekrar deneyin.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  const checkAuth = async () => {
    const epoch = authEpoch;
    setLoading(true); setAuthError('');
    const access = getAccessToken();
    const refresh = getRefreshToken();
    if (!access && !refresh) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${BACKEND}/api/auth/me`, { timeout: 15000 });
      if (epoch !== authEpoch) return;
      bindWorkspaceOwner(data.id);
      setUser(withAccess(data));
    } catch (error) {
      if (epoch !== authEpoch) { if (!getAccessToken() && !getRefreshToken()) { setUser(false); setLoading(false); } return; }
      if (getAccessToken() || getRefreshToken()) setAuthError('Bağlantı kurulamadı. Oturumun korundu; tekrar deneyebilirsin.');
      else setUser(false);
    } finally {
      if (epoch === authEpoch) setLoading(false);
    }
  };

  useEffect(() => {
    // A previous session check must not overwrite the incoming Google session.
    if (window.location.pathname === '/auth/google') {
      clearTokens();
      clearWorkspace();
      setUser(false);
      setLoading(false);
      return;
    }
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const changed = event => {
      if (event.key === 'mp_workspace_owner' || (event.key === REFRESH_KEY && event.newValue)) {
        invalidateAuthWork(); clearWorkspace(); checkAuth();
      } else if ((event.key === ACCESS_KEY || event.key === REFRESH_KEY) && !event.newValue) {
        invalidateAuthWork(); clearWorkspace(); setAuthError(''); setUser(false); setLoading(false);
      }
      // Another tab refreshing the same family only changes ACCESS_KEY; it must
      // not cancel our identity check or leave the loading state permanently set.
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A5b: profil kapısı yalnız mekân/organizatör hesabında; personel/admin/bireyselde sunucu null döner.
  const applyProfileGate = (gate) => { gateEpoch += 1; setUser(current => (current && ['venue', 'organizer'].includes(current.role)
    ? { ...current, profile_gate: gate || null } : current)); };
  const refreshProfileGate = async () => {
    try {
      const { data } = await axios.get(`${BACKEND}/api/auth/me`, { timeout: 15000 });
      if (data && Object.prototype.hasOwnProperty.call(data, 'profile_gate')) applyProfileGate(data.profile_gate);
      return data?.profile_gate ?? null;
    } catch { return undefined; }
  };
  useEffect(() => {
    const required = event => applyProfileGate(event.detail);
    window.addEventListener(PROFILE_GATE_EVENT, required);
    return () => window.removeEventListener(PROFILE_GATE_EVENT, required);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isGatewayError = (error) =>
    !error.response?.data?.detail && [404, 502, 503, 504].includes(error.response?.status);

  const postWithColdStartRetry = async (url, body, attempts = 4) => {
    const deadline = Date.now() + 30000;
    for (let i = 0; ; i++) {
      try {
        const remaining = deadline - Date.now();
        if (remaining <= 0) throw Object.assign(new Error('İstek zaman aşımına uğradı.'), { code: 'ECONNABORTED' });
        return await axios.post(url, body, { timeout: Math.min(15000, remaining) });
      } catch (error) {
        const delay = 2000 * (i + 1);
        if (i >= attempts - 1 || !isGatewayError(error) || Date.now() + delay >= deadline) throw error;
        await new Promise(r => setTimeout(r, delay));
      }
    }
  };

  const login = async (email, password) => {
    invalidateAuthWork();
    const epoch = authEpoch;
    try {
      const { data } = await postWithColdStartRetry(
        `${BACKEND}/api/auth/login`,
        { email, password }
      );
      if (epoch !== authEpoch) return { success: false, error: 'Oturum değişti; tekrar giriş yap.' };
      setTokens(data.access_token, data.refresh_token);
      bindWorkspaceOwner(data.user?.id);
      setUser(withAccess(data.user)); setLoading(false); setAuthError('');
      return { success: true, user: withAccess(data.user) };
    } catch (error) {

      let msg;
      if (error.response?.data?.detail) {
        msg = formatApiErrorDetail(error.response.data.detail);
      } else if (isGatewayError(error)) {
        msg = 'Sunucu başlatılıyor, lütfen 10-15 saniye sonra tekrar deneyin.';
      } else if (error.response?.status === 500) {
        msg = 'Sunucu hatası. Birazdan tekrar deneyin.';
      } else if (error.response?.status) {
        msg = `Sunucu hatası (${error.response.status}). Lütfen tekrar deneyin.`;
      } else if (['ECONNABORTED', 'ETIMEDOUT'].includes(error.code)) {
        msg = 'Sunucu zamanında yanıt vermedi. Bilgilerin korundu; tekrar giriş yapabilirsin.';
      } else if (error.message === 'Network Error') {
        msg = 'Ağ hatası. İnternet bağlantınızı kontrol edin.';
      } else {
        msg = error.message || 'Bir hata oluştu. Lütfen tekrar deneyin.';
      }
      return { success: false, error: msg };
    }
  };

  // A11: kayıt, davetle kayıt, Google ve personel daveti yanıtları üyelik kilidi / profil kapısı / yetki özetini
  // taşımayabilir. Oturum açılır açılmaz /auth/me ile tamamlanır; böylece ilk oturumda da kilit kartları ve
  // profil kapısı girişteki gibi baştan görünür. Gelmezse mevcut oturum korunur (sunucu her uçta ayrıca denetler).
  const hydrateSession = async (epoch, id) => {
    const gate = gateEpoch;
    try {
      const { data } = await axios.get(`${BACKEND}/api/auth/me`, { timeout: 15000 });
      if (epoch !== authEpoch || !data || !id || data.id !== id) return;
      // Bu sırada kapı güncellendiyse (ör. profil gönderildi) yeni kapı korunur; geri kalan özet alınır.
      setUser(current => gate === gateEpoch || !current ? withAccess(data) : { ...withAccess(data), profile_gate: current.profile_gate ?? null });
    } catch { /* özet tamamlanamadı; oturum ve sunucu denetimleri değişmez */ }
  };

  const register = async (email, password, name, role) => {
    invalidateAuthWork();
    const epoch = authEpoch;
    try {
      const { data } = await postWithColdStartRetry(
        `${BACKEND}/api/auth/register`,
        { email, password, name, role }
      );
      if (epoch !== authEpoch) return { success: false, error: 'Oturum değişti; tekrar giriş yap.' };
      setTokens(data.access_token, data.refresh_token);
      bindWorkspaceOwner(data.user?.id);
      setUser(withAccess(data.user)); setLoading(false); setAuthError('');
      hydrateSession(epoch, data.user?.id);
      return { success: true, user: withAccess(data.user) };
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.code === 'EMAIL_ALREADY_REGISTERED' || (typeof detail === 'string' && /email already registered|e-posta zaten kayıtlı/i.test(detail))) {
        return { success: false, code: 'EMAIL_ALREADY_REGISTERED', error: 'Bu e-posta adresiyle bir hesap var. Giriş yapabilir veya şifreni oluşturmak / yenilemek için bağlantı isteyebilirsin.' };
      }
      let msg;
      if (error.response?.data?.detail) {
        msg = formatApiErrorDetail(error.response.data.detail);
      } else if (isGatewayError(error)) {
        msg = 'Sunucu başlatılıyor, lütfen 10-15 saniye sonra tekrar deneyin.';
      } else if (error.response?.status === 500) {
        msg = 'Sunucu hatası. Birazdan tekrar deneyin.';
      } else if (error.response?.status) {
        msg = `Sunucu hatası (${error.response.status}). Lütfen tekrar deneyin.`;
      } else if (['ECONNABORTED', 'ETIMEDOUT'].includes(error.code)) {
        return { success: false, code: 'REGISTRATION_UNCONFIRMED', error: 'Kayıt sonucu doğrulanamadı. Hesabın oluşmuş olabilir; önce giriş yapmayı dene. Bilgilerin bu formda korundu.' };
      } else if (error.message === 'Network Error') {
        msg = 'Ağ hatası. İnternet bağlantınızı kontrol edin.';
      } else {
        msg = error.message || 'Bir hata oluştu. Lütfen tekrar deneyin.';
      }
      return { success: false, error: msg };
    }
  };

  // Any server-issued session (Google, staff invite) replaces the previous one.
  const acceptSession = (data) => {
    clearTokens();
    const epoch = authEpoch;
    setTokens(data.access_token, data.refresh_token);
    bindWorkspaceOwner(data.user?.id);
    setUser(withAccess(data.user)); setLoading(false); setAuthError('');
    hydrateSession(epoch, data.user?.id);
  };
  const acceptGoogleSession = acceptSession;
  const can = (permission) => hasPermission(user, permission);

  const logout = async () => {
    const access = getAccessToken(), refresh = getRefreshToken();
    clearTokens(); clearWorkspace(); safeSessionStorage.removeItem('mp_return_to');
    setUser(false); setLoading(false); setAuthError('');
    try {
      await axios.post(`${BACKEND}/api/auth/logout`, { refresh_token: refresh }, {
        headers: access ? { Authorization: `Bearer ${access}` } : {}, _skipAuthRefresh: true
      });
    } catch {
      toast.error('Bu cihazdan çıkış yaptın; sunucuda oturum kapatma doğrulanamadı.');
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, authError, login, register, logout, checkAuth, acceptGoogleSession, acceptSession, can, applyProfileGate, refreshProfileGate }}>
      {storageDegraded() && <p role="status" className="px-4 py-2 text-sm">Tarayıcı depolaması kullanılamıyor; oturumun ve gönderilmemiş taslakların bu pencere açıkken korunur.</p>}
      {children}
    </AuthContext.Provider>
  );
};
